import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { GraduationCap, Plus, Edit, Trash2, Upload, Search, BookOpen, Layers, HelpCircle, ChevronLeft, ChevronRight, Save, FileUp, AlertCircle, CheckCircle, X, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const PromotionQuestionBank = () => {
  const [activeTab, setActiveTab] = useState('ranks');
  const [ranks, setRanks] = useState([]);
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedRank, setSelectedRank] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  // Dialogs
  const [rankDialog, setRankDialog] = useState(false);
  const [topicDialog, setTopicDialog] = useState(false);
  const [questionDialog, setQuestionDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [rankForm, setRankForm] = useState({ name: '', description: '', exam_duration_minutes: 60, num_questions: 60 });
  const [topicForm, setTopicForm] = useState({ rank_id: '', name: '', description: '' });
  const [questionForm, setQuestionForm] = useState({
    rank_id: '', year: '', topic_id: '', question_text: '',
    option_a: '', option_b: '', option_c: '', option_d: '',
    correct_option: '', explanation: '', difficulty: 'medium'
  });
  const [importFile, setImportFile] = useState(null);
  const [importRank, setImportRank] = useState('');
  const [importYear, setImportYear] = useState('');
  const [importQType, setImportQType] = useState('multiple_choice');
  const [importPreview, setImportPreview] = useState(null);
  const [importStep, setImportStep] = useState('form'); // form, preview, done
  const [saving, setSaving] = useState(false);

  // Bulk question builder
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRank, setBulkRank] = useState('');
  const [bulkYear, setBulkYear] = useState('');
  const [bulkQuestions, setBulkQuestions] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Resources management
  const [adminResources, setAdminResources] = useState([]);
  const [resourceCats, setResourceCats] = useState([]);
  const [resForm, setResForm] = useState({ category: '', title: '', content: '' });
  const [editingRes, setEditingRes] = useState(null);
  const [resSaving, setResSaving] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState('');

  const fetchRanks = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/ranks`, { headers: getAuthHeaders() });
      if (res.ok) setRanks(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchTopics = useCallback(async (rankId) => {
    try {
      const url = rankId ? `${API_BASE}/api/admin/promotion/topics?rank_id=${rankId}` : `${API_BASE}/api/admin/promotion/topics`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) setTopics(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/admin/promotion/questions?page=${page}&limit=20`;
      if (selectedRank && selectedRank !== 'all') url += `&rank_id=${selectedRank}`;
      if (selectedYear && selectedYear !== 'all') url += `&year=${selectedYear}`;
      if (selectedTopic && selectedTopic !== 'all') url += `&topic_id=${selectedTopic}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setQuestions(data.questions);
        setTotalQuestions(data.total);
      } else {
        toast.error('Failed to load questions');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, selectedRank, selectedYear, selectedTopic]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/stats`, { headers: getAuthHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/analytics`, { headers: getAuthHeaders() });
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchRanks(); fetchStats(); fetchAnalytics(); }, [fetchRanks, fetchStats]);
  useEffect(() => { if (activeTab === 'questions') fetchQuestions(); }, [activeTab, fetchQuestions]);
  useEffect(() => { fetchTopics(selectedRank); }, [selectedRank, fetchTopics]);

  const fetchAdminResources = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/resources`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAdminResources(data.resources || []);
        setResourceCats(data.categories || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (activeTab === 'resources') fetchAdminResources(); }, [activeTab, fetchAdminResources]);

  const saveResource = async () => {
    if (!resForm.category || !resForm.title || !resForm.content) { toast.error('All fields are required'); return; }
    setResSaving(true);
    try {
      const url = editingRes
        ? `${API_BASE}/api/admin/promotion/resources/${editingRes.id}`
        : `${API_BASE}/api/admin/promotion/resources`;
      const res = await fetch(url, {
        method: editingRes ? 'PUT' : 'POST', headers: getAuthHeaders(),
        body: JSON.stringify(resForm)
      });
      if (res.ok) {
        toast.success(editingRes ? 'Resource updated' : 'Resource created');
        setResForm({ category: '', title: '', content: '' }); setEditingRes(null);
        fetchAdminResources();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error saving resource'); }
    setResSaving(false);
  };

  const deleteResource = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/resources/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Resource deleted'); fetchAdminResources(); }
    } catch (e) { toast.error('Error'); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) { toast.error('Enter a category name'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/resource-categories`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ name: newCatName.trim() })
      });
      if (res.ok) { toast.success('Category added'); setNewCatName(''); fetchAdminResources(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Delete this category? All resources under it must be deleted first.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/resource-categories/${catId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Category deleted'); fetchAdminResources(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
  };

  const updateCategory = async (catId) => {
    if (!editCatName.trim()) { toast.error('Category name cannot be empty'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/resource-categories/${catId}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ name: editCatName.trim() })
      });
      if (res.ok) { toast.success('Category renamed'); setEditingCat(null); setEditCatName(''); fetchAdminResources(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error renaming category'); }
  };



  // RANK CRUD
  const saveRank = async () => {
    setSaving(true);
    try {
      const url = editingItem
        ? `${API_BASE}/api/admin/promotion/ranks/${editingItem.id}`
        : `${API_BASE}/api/admin/promotion/ranks`;
      const res = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...rankForm,
          exam_duration_minutes: rankForm.exam_duration_minutes || 60,
          num_questions: rankForm.num_questions || 60
        })
      });
      if (res.ok) {
        toast.success(editingItem ? 'Rank updated' : 'Rank created');
        setRankDialog(false); setEditingItem(null);
        setRankForm({ name: '', description: '', exam_duration_minutes: 60, num_questions: 60 });
        fetchRanks(); fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch (e) { toast.error('Error saving rank'); }
    setSaving(false);
  };

  const deleteRank = async (id) => {
    if (!window.confirm('Delete this rank?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/ranks/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Rank deleted'); fetchRanks(); fetchStats(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
  };

  // TOPIC CRUD
  const saveTopic = async () => {
    setSaving(true);
    try {
      const url = editingItem
        ? `${API_BASE}/api/admin/promotion/topics/${editingItem.id}`
        : `${API_BASE}/api/admin/promotion/topics`;
      const res = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(topicForm)
      });
      if (res.ok) {
        toast.success(editingItem ? 'Topic updated' : 'Topic created');
        setTopicDialog(false); setEditingItem(null);
        setTopicForm({ rank_id: '', name: '', description: '' });
        fetchTopics(selectedRank);
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSaving(false);
  };

  const deleteTopic = async (id) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/topics/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Deleted'); fetchTopics(selectedRank); }
    } catch (e) { toast.error('Error'); }
  };

  // QUESTION CRUD
  const saveQuestion = async () => {
    if (!questionForm.rank_id || !questionForm.year || !questionForm.question_text ||
        !questionForm.option_a || !questionForm.option_b || !questionForm.option_c ||
        !questionForm.option_d || !questionForm.correct_option) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const url = editingItem
        ? `${API_BASE}/api/admin/promotion/questions/${editingItem.id}`
        : `${API_BASE}/api/admin/promotion/questions`;
      const body = editingItem ? {
        ...questionForm,
        year: parseInt(questionForm.year)
      } : {
        ...questionForm,
        year: parseInt(questionForm.year)
      };
      const res = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(editingItem ? 'Question updated' : 'Question created');
        setQuestionDialog(false); setEditingItem(null);
        setQuestionForm({ rank_id: '', year: '', topic_id: '', question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: '', explanation: '', difficulty: 'medium' });
        fetchQuestions(); fetchStats();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error'); }
    setSaving(false);
  };

  const emptyQuestion = (num) => ({
    question_number: num, question_type: 'multiple_choice', question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
    correct_option: '', explanation: '', difficulty: 'medium', topic_id: ''
  });

  const refreshStartNumber = useCallback(async (rk, yr) => {
    if (!rk || !yr || yr === 'all') return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/questions?rank_id=${rk}&year=${yr}&page=1&limit=1`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const startNum = (data.max_question_number || 0) + 1;
        setBulkQuestions([emptyQuestion(startNum)]);
      }
    } catch (e) { /* keep current */ }
  }, []);

  const openBulkBuilder = async () => {
    const rk = selectedRank || '';
    const yr = selectedYear || '';
    setBulkRank(rk);
    setBulkYear(yr);
    setBulkMode(true);

    // Fetch max question number for this rank/year to continue numbering
    let startNum = 1;
    if (rk && yr && yr !== 'all') {
      try {
        const res = await fetch(`${API_BASE}/api/admin/promotion/questions?rank_id=${rk}&year=${yr}&page=1&limit=1`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          startNum = (data.max_question_number || 0) + 1;
        }
      } catch (e) { /* fallback to 1 */ }
    }
    setBulkQuestions([emptyQuestion(startNum)]);
  };

  const addBulkQuestion = () => {
    setBulkQuestions(prev => {
      const lastNum = prev.length > 0 ? Math.max(...prev.map(q => q.question_number)) : 0;
      return [...prev, emptyQuestion(lastNum + 1)];
    });
  };

  const removeBulkQuestion = (index) => {
    setBulkQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const duplicateBulkQuestion = (index) => {
    setBulkQuestions(prev => {
      const lastNum = Math.max(...prev.map(q => q.question_number));
      const copy = { ...prev[index], question_number: lastNum + 1 };
      return [...prev, copy];
    });
  };

  const updateBulkQuestion = (index, field, value) => {
    setBulkQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
  };

  const countValidQuestions = () => bulkQuestions.filter(q => {
    if (!q.question_text.trim() || !q.correct_option) return false;
    if (q.question_type === 'true_false') return ['A','B'].includes(q.correct_option);
    if (q.question_type === 'fill_in') return q.correct_option.trim().length > 0;
    return q.option_a.trim() && q.option_b.trim();
  }).length;


  const saveBulkQuestions = async () => {
    if (!bulkRank || !bulkYear) { toast.error('Select rank and year first'); return; }
    const isComplete = (q) => {
      if (!q.question_text.trim() || !q.correct_option) return false;
      if (q.question_type === 'true_false') return ['A','B'].includes(q.correct_option);
      if (q.question_type === 'fill_in') return q.correct_option.trim().length > 0;
      return q.option_a.trim() && q.option_b.trim();
    };
    const valid = bulkQuestions.filter(isComplete);
    if (valid.length === 0) { toast.error('No complete questions to save. Fill question text, all 4 options, and correct answer.'); return; }

    setBulkSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/questions/bulk-create`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ rank_id: bulkRank, year: parseInt(bulkYear), questions: valid })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.created} question(s) saved!`);
        if (data.errors?.length) toast.warning(`${data.errors.length} skipped: ${data.errors[0]}`);
        setSelectedRank(bulkRank);
        setSelectedYear(String(bulkYear));
        setPage(1);
        setBulkMode(false);
        setBulkQuestions([]);
        fetchQuestions(); fetchStats();
      } else { const err = await res.json(); toast.error(err.detail || 'Save failed'); }
    } catch (e) { toast.error('Error saving questions'); }
    setBulkSaving(false);
  };


  const deleteQuestion = async (id) => {
    if (!window.confirm('Deactivate this question?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/promotion/questions/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Question deactivated'); fetchQuestions(); fetchStats(); }
    } catch (e) { toast.error('Error'); }
  };

  // BULK IMPORT
  const handleImportPreview = async () => {
    if (!importFile || !importRank || !importYear) { toast.error('Select file, rank, and year'); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('rank_id', importRank);
      formData.append('year', importYear);
      formData.append('question_type', importQType);
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/api/admin/promotion/questions/csv-preview`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setImportPreview(data);
        setImportStep('preview');
      } else { const err = await res.json(); toast.error(err.detail || 'Preview failed'); }
    } catch (e) { toast.error('Preview error'); }
    setSaving(false);
  };

  const handleImportConfirm = async () => {
    if (!importFile || !importRank || !importYear) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('rank_id', importRank);
      formData.append('year', importYear);
      formData.append('question_type', importQType);
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/api/admin/promotion/questions/bulk-import`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`${data.imported} questions imported (Q${data.start_number}–Q${data.end_number})`);
          setImportStep('form'); setImportPreview(null); setImportFile(null);
          setImportDialog(false);
          setSelectedRank(importRank); setSelectedYear(importYear); setPage(1);
          fetchQuestions(); fetchStats();
        } else {
          toast.error(data.message || 'Import failed — validation errors');
          setImportPreview(data); setImportStep('preview');
        }
      } else { const err = await res.json(); toast.error(err.detail || 'Import failed'); }
    } catch (e) { toast.error('Import error'); }
    setSaving(false);
  };

  const getRankName = (id) => ranks.find(r => r.id === id)?.name || 'Unknown';
  const getTopicName = (id) => topics.find(t => t.id === id)?.name || '-';
  const totalPages = Math.ceil(totalQuestions / 20);

  return (
    <div className="space-y-4">
      {/* BULK QUESTION BUILDER */}
      {bulkMode && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Plus className="h-6 w-6 text-blue-600" />
                Add Multiple Questions
              </h2>
              <p className="text-sm text-gray-500">Create all questions for a rank/year at once. Save at any point.</p>
            </div>
            <Button variant="outline" onClick={() => setBulkMode(false)} data-testid="bulk-back-btn">
              <X className="h-4 w-4 mr-1" />Back to Question Bank
            </Button>
          </div>

          {/* Rank & Year selectors */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <Label>Rank *</Label>
                  <Select value={bulkRank} onValueChange={v => { setBulkRank(v); if (bulkYear) refreshStartNumber(v, bulkYear); }}>
                    <SelectTrigger data-testid="bulk-rank-select"><SelectValue placeholder="Select rank" /></SelectTrigger>
                    <SelectContent>{ranks.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year *</Label>
                  <Select value={String(bulkYear)} onValueChange={v => { setBulkYear(v); if (bulkRank) refreshStartNumber(bulkRank, v); }}>
                    <SelectTrigger data-testid="bulk-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent>{[2020,2021,2022,2023,2024,2025,2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveBulkQuestions} disabled={bulkSaving} className="flex-1" data-testid="save-bulk-btn">
                    <Save className="h-4 w-4 mr-1" />{bulkSaving ? 'Saving...' : `Save ${countValidQuestions()} Question(s)`}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {bulkQuestions.length} question(s) in list.
                Only complete questions will be saved. Multi-choice needs text + 2+ options + correct answer. True/False and Fill-in need text + correct answer.
              </p>
            </CardContent>
          </Card>

          {/* Question Cards */}
          <div className="space-y-3">
            {bulkQuestions.map((q, idx) => (
              <Card key={idx} data-testid={`bulk-question-card-${idx}`} className={`border-l-4 ${q.question_text.trim() && q.correct_option ? 'border-l-green-400' : 'border-l-gray-300'}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span data-testid={`bulk-question-number-${idx}`} className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">{q.question_number}</span>
                      <span className="text-sm font-semibold text-gray-700">Question {q.question_number}</span>
                      {q.question_text.trim() && q.correct_option && <CheckCircle className="h-4 w-4 text-green-500" />}
                    </div>
                    <div className="flex gap-1 items-center">
                      {/* Question Type */}
                      <Select value={q.question_type} onValueChange={v => {
                        updateBulkQuestion(idx, 'question_type', v);
                        if (v === 'true_false') { updateBulkQuestion(idx, 'option_a', 'True'); updateBulkQuestion(idx, 'option_b', 'False'); updateBulkQuestion(idx, 'option_c', ''); updateBulkQuestion(idx, 'option_d', ''); updateBulkQuestion(idx, 'correct_option', ''); }
                        if (v === 'fill_in') { updateBulkQuestion(idx, 'option_a', ''); updateBulkQuestion(idx, 'option_b', ''); updateBulkQuestion(idx, 'option_c', ''); updateBulkQuestion(idx, 'option_d', ''); updateBulkQuestion(idx, 'correct_option', ''); }
                        if (v === 'multiple_choice') { updateBulkQuestion(idx, 'correct_option', ''); }
                      }}>
                        <SelectTrigger className="w-36 h-7 text-xs" data-testid={`bulk-question-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="multiple_choice" data-testid={`bulk-qtype-multiple_choice-${idx}`}>Multi-Choice</SelectItem>
                          <SelectItem value="true_false" data-testid={`bulk-qtype-true_false-${idx}`}>True / False</SelectItem>
                          <SelectItem value="fill_in" data-testid={`bulk-qtype-fill_in-${idx}`}>Fill in the Gap</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" title="Duplicate" data-testid={`bulk-duplicate-question-${idx}`} onClick={() => duplicateBulkQuestion(idx)}>
                        <Copy className="h-3.5 w-3.5 text-gray-500" />
                      </Button>
                      {bulkQuestions.length > 1 && (
                        <Button size="sm" variant="ghost" title="Remove" data-testid={`bulk-remove-question-${idx}`} onClick={() => removeBulkQuestion(idx)}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Question Text *</Label>
                    <textarea
                      className="w-full border rounded-md p-2 min-h-[60px] text-sm mt-1"
                      data-testid={`bulk-question-text-${idx}`}
                      value={q.question_text}
                      onChange={e => updateBulkQuestion(idx, 'question_text', e.target.value)}
                      placeholder={q.question_type === 'fill_in' ? 'The capital of Ghana is ___.' : `Enter question ${q.question_number}...`}
                    />
                  </div>

                  {/* Options — conditional on question type */}
                  {q.question_type === 'multiple_choice' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {['A', 'B', 'C', 'D'].map(opt => (
                        <div key={opt} className="flex items-center gap-2">
                          <button type="button" onClick={() => updateBulkQuestion(idx, 'correct_option', opt)}
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${q.correct_option === opt ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                            title={`Set ${opt} as correct`}>{opt}</button>
                          <Input data-testid={`bulk-option-${opt.toLowerCase()}-${idx}`}
                            value={q[`option_${opt.toLowerCase()}`]}
                            onChange={e => updateBulkQuestion(idx, `option_${opt.toLowerCase()}`, e.target.value)}
                            placeholder={`Option ${opt}${['A','B'].includes(opt) ? ' *' : ' (optional)'}`}
                            className="flex-1 text-sm h-9" />
                        </div>
                      ))}
                    </div>
                  )}

                  {q.question_type === 'true_false' && (
                    <div className="flex gap-3">
                      {[{key:'A', label:'True'}, {key:'B', label:'False'}].map(({key, label}) => (
                        <button key={key} type="button" data-testid={`bulk-tf-${key.toLowerCase()}-${idx}`} onClick={() => updateBulkQuestion(idx, 'correct_option', key)}
                          className={`flex-1 p-3 rounded-xl border-2 text-center font-semibold text-sm transition-all ${q.correct_option === key ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                        >{label}</button>
                      ))}
                    </div>
                  )}

                  {q.question_type === 'fill_in' && (
                    <div>
                      <Label className="text-xs">Correct Answer *</Label>
                      <Input value={q.correct_option} data-testid={`bulk-fill-answer-${idx}`} onChange={e => updateBulkQuestion(idx, 'correct_option', e.target.value)}
                        placeholder="Type the correct answer..." className="text-sm mt-1" />
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {q.question_type === 'multiple_choice' && (
                      <div>
                        <Label className="text-xs">Correct Answer</Label>
                        <div className="flex gap-1 mt-1">
                          {['A', 'B', 'C', 'D'].map(opt => (
                            <button key={opt} type="button" data-testid={`bulk-correct-${opt.toLowerCase()}-${idx}`}
                              className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${q.correct_option === opt ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              onClick={() => updateBulkQuestion(idx, 'correct_option', opt)}
                            >{opt}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Difficulty</Label>
                      <Select value={q.difficulty} onValueChange={v => updateBulkQuestion(idx, 'difficulty', v)}>
                        <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="easy">Easy</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="hard">Hard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-xs">Explanation (optional)</Label>
                      <Input value={q.explanation} onChange={e => updateBulkQuestion(idx, 'explanation', e.target.value)}
                        placeholder="Why this is correct..." className="text-sm h-8 mt-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add more + Save */}
          <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-white/95 backdrop-blur p-3 border rounded-xl shadow-lg">
            <Button variant="outline" onClick={addBulkQuestion} data-testid="add-another-question-btn">
              <Plus className="h-4 w-4 mr-1" />Add Question {bulkQuestions.length > 0 ? Math.max(...bulkQuestions.map(q => q.question_number)) + 1 : 1}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkMode(false)}>Cancel</Button>
              <Button onClick={saveBulkQuestions} disabled={bulkSaving} data-testid="save-bulk-bottom-btn">
                <Save className="h-4 w-4 mr-1" />{bulkSaving ? 'Saving...' : `Save ${countValidQuestions()} Question(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* NORMAL VIEW */}
      {!bulkMode && <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-amber-600" />
            Promotion Question Bank
          </h2>
          <p className="text-sm text-gray-500">Manage promotion examination ranks, topics, and questions</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.total_questions}</p>
            <p className="text-xs text-gray-500">Total Questions</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.ranks?.length || 0}</p>
            <p className="text-xs text-gray-500">Active Ranks</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.total_users_preparing}</p>
            <p className="text-xs text-gray-500">Users Preparing</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.ranks?.reduce((acc, r) => acc + r.years.length, 0) || 0}</p>
            <p className="text-xs text-gray-500">Year-Rank Combos</p>
          </CardContent></Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="ranks" data-testid="promo-tab-ranks"><Layers className="h-4 w-4 mr-1" />Ranks</TabsTrigger>
          <TabsTrigger value="topics" data-testid="promo-tab-topics"><BookOpen className="h-4 w-4 mr-1" />Topics</TabsTrigger>
          <TabsTrigger value="questions" data-testid="promo-tab-questions"><HelpCircle className="h-4 w-4 mr-1" />Questions</TabsTrigger>
          <TabsTrigger value="resources" data-testid="promo-tab-resources"><FileUp className="h-4 w-4 mr-1" />Resources</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="promo-tab-analytics"><Search className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
        </TabsList>

        {/* RANKS TAB */}
        <TabsContent value="ranks">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Promotion Ranks</CardTitle>
                <CardDescription>Configure promotion examination ranks</CardDescription>
              </div>
              <Button size="sm" onClick={() => { setEditingItem(null); setRankForm({ name: '', description: '', exam_duration_minutes: 60, num_questions: 60 }); setRankDialog(true); }}>
                <Plus className="h-4 w-4 mr-1" />Add Rank
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {ranks.map((rank, i) => (
                  <div key={rank.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">{i + 1}</span>
                      <div>
                        <p className="font-medium">{rank.name}</p>
                        <p className="text-xs text-gray-500">{rank.description || 'No description'} | {rank.exam_duration_minutes} min | {rank.num_questions} questions</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={rank.active ? 'default' : 'secondary'}>{rank.active ? 'Active' : 'Inactive'}</Badge>
                      <Button size="sm" variant="outline" onClick={() => { setEditingItem(rank); setRankForm({ name: rank.name, description: rank.description || '', exam_duration_minutes: rank.exam_duration_minutes, num_questions: rank.num_questions }); setRankDialog(true); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteRank(rank.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {ranks.length === 0 && <p className="text-center text-gray-500 py-8">No ranks created yet. Click &ldquo;Add Rank&rdquo; to get started.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TOPICS TAB */}
        <TabsContent value="topics">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Question Topics</CardTitle>
                <CardDescription>Organize questions by topic/category</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={selectedRank} onValueChange={setSelectedRank}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="Filter by rank" /></SelectTrigger>
                  <SelectContent>{ranks.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" onClick={() => { setEditingItem(null); setTopicForm({ rank_id: selectedRank || '', name: '', description: '' }); setTopicDialog(true); }}>
                  <Plus className="h-4 w-4 mr-1" />Add Topic
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {topics.map(topic => (
                  <div key={topic.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{topic.name}</p>
                      <p className="text-xs text-gray-500">{getRankName(topic.rank_id)} | {topic.description || 'No description'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => { setEditingItem(topic); setTopicForm({ rank_id: topic.rank_id, name: topic.name, description: topic.description || '' }); setTopicDialog(true); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTopic(topic.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {topics.length === 0 && <p className="text-center text-gray-500 py-8">{selectedRank ? 'No topics for this rank.' : 'Select a rank to view topics.'}</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* QUESTIONS TAB */}
        <TabsContent value="questions">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-3 justify-between">
                <div>
                  <CardTitle className="text-base">Questions ({totalQuestions})</CardTitle>
                  <CardDescription>Manage examination questions</CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={openBulkBuilder} data-testid="add-questions-btn">
                    <Plus className="h-4 w-4 mr-1" />Add Questions
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setImportDialog(true)} data-testid="import-csv-btn">
                    <FileUp className="h-4 w-4 mr-1" />Import CSV
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap mt-3">
                <Select value={selectedRank} onValueChange={(v) => { setSelectedRank(v); setPage(1); }}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="All Ranks" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ranks</SelectItem>
                    {ranks.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={(v) => { setSelectedYear(v); setPage(1); }}>
                  <SelectTrigger className="w-32"><SelectValue placeholder="All Years" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {[2020, 2021, 2022, 2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-gray-500">Loading...</p>
              ) : (
                <div className="space-y-3">
                  {questions.map((q, i) => (
                    <div key={q.id} className="p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm mb-1 line-clamp-2">
                            {q.question_number ? <span className="text-blue-600 font-bold mr-1">Q{q.question_number}.</span> : null}
                            {q.question_text}
                          </p>
                          <div className="flex gap-1 flex-wrap">
                            <Badge variant="outline" className="text-xs">{getRankName(q.rank_id)}</Badge>
                            <Badge variant="outline" className="text-xs bg-blue-50">{q.year}</Badge>
                            <Badge variant="outline" className="text-xs bg-green-50">Answer: {q.correct_option}</Badge>
                            <Badge variant="outline" className="text-xs">{q.difficulty}</Badge>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => {
                            setEditingItem(q);
                            setQuestionForm({
                              rank_id: q.rank_id, year: String(q.year), topic_id: q.topic_id || '',
                              question_text: q.question_text,
                              option_a: q.options?.A || '', option_b: q.options?.B || '',
                              option_c: q.options?.C || '', option_d: q.options?.D || '',
                              correct_option: q.correct_option, explanation: q.explanation || '',
                              difficulty: q.difficulty || 'medium'
                            });
                            setQuestionDialog(true);
                          }}><Edit className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteQuestion(q.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                        {Object.entries(q.options || {}).map(([key, val]) => (
                          <span key={key} className={`px-2 py-1 rounded ${key === q.correct_option ? 'bg-green-100 text-green-800 font-medium' : 'bg-gray-50 text-gray-600'}`}>
                            {key}. {val}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {questions.length === 0 && <p className="text-center text-gray-500 py-8">No questions found. Add questions or import from CSV.</p>}
                </div>
              )}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* RESOURCES TAB */}
        <TabsContent value="resources">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{editingRes ? 'Edit Resource' : 'Add Learning Resource'}</CardTitle>
                <CardDescription>This covers the six core areas from which GES sets Promotion Exams questions. Create study materials for subscribed users under these learning categories.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Category *</Label>
                  <Select value={resForm.category} onValueChange={v => setResForm(p => ({...p, category: v}))}>
                    <SelectTrigger data-testid="resource-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {resourceCats.map(c => <SelectItem key={c.id} value={c.id} data-testid={`resource-category-option-${c.id}`}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input data-testid="resource-title-input" value={resForm.title} onChange={e => setResForm(p => ({...p, title: e.target.value}))} placeholder="e.g., Key Provisions of the 2020 Education Act" />
                </div>
                <div>
                  <Label>Content *</Label>
                  <textarea data-testid="resource-content-input" className="w-full border rounded-md p-3 min-h-[200px] text-sm" value={resForm.content} onChange={e => setResForm(p => ({...p, content: e.target.value}))} placeholder="Paste or type the learning resource content here..." />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveResource} disabled={resSaving} data-testid="save-resource-btn">
                    <Save className="h-4 w-4 mr-1" />{resSaving ? 'Saving...' : editingRes ? 'Update Resource' : 'Save Resource'}
                  </Button>
                  {editingRes && (
                    <Button variant="outline" onClick={() => { setEditingRes(null); setResForm({ category: '', title: '', content: '' }); }}>Cancel Edit</Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Category Management */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Manage Categories</CardTitle>
                <CardDescription>Add, rename or remove learning resource categories</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name..." className="flex-1" data-testid="new-category-input" />
                  <Button size="sm" onClick={addCategory} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
                </div>
                <div className="space-y-2">
                  {resourceCats.map(cat => (
                    <div key={cat.id} className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50">
                      {editingCat === cat.id ? (
                        <>
                          <Input
                            value={editCatName}
                            onChange={e => setEditCatName(e.target.value)}
                            className="flex-1 h-8 text-sm"
                            data-testid={`edit-category-input-${cat.id}`}
                            onKeyDown={e => { if (e.key === 'Enter') updateCategory(cat.id); if (e.key === 'Escape') { setEditingCat(null); setEditCatName(''); } }}
                            autoFocus
                          />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50" onClick={() => updateCategory(cat.id)} data-testid={`save-category-${cat.id}`}>
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-500 hover:bg-gray-100" onClick={() => { setEditingCat(null); setEditCatName(''); }}>
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium">{cat.name}</span>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500 hover:bg-blue-50" onClick={() => { setEditingCat(cat.id); setEditCatName(cat.name); }} title="Rename category" data-testid={`edit-category-${cat.id}`}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => deleteCategory(cat.id)} title="Delete category" data-testid={`delete-category-${cat.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                  {resourceCats.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-2">No categories yet. Add one above.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Existing Resources by Category */}
            {resourceCats.map(cat => {
              const catRes = adminResources.filter(r => r.category === cat.id);
              if (catRes.length === 0) return null;
              return (
                <Card key={cat.id}>
                  <CardHeader>
                    <CardTitle className="text-sm text-teal-700">{cat.name} ({catRes.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {catRes.map(res => (
                        <div key={res.id} data-testid={`admin-resource-item-${res.id}`} className="p-3 border rounded-lg hover:bg-gray-50">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{res.title}</p>
                              <p className="text-xs text-gray-500 line-clamp-2 mt-1">{res.content.substring(0, 150)}...</p>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button size="sm" variant="outline" onClick={() => { setEditingRes(res); setResForm({ category: res.category, title: res.title, content: res.content }); }}>
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteResource(res.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics">
          {analytics ? (
            <div className="space-y-4">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{analytics.overview?.total_users_preparing || 0}</p>
                  <p className="text-xs text-gray-500">Users Preparing</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{analytics.overview?.total_attempts || 0}</p>
                  <p className="text-xs text-gray-500">Total Attempts</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{analytics.overview?.active_subscriptions || 0}</p>
                  <p className="text-xs text-gray-500">Active Subs</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-teal-600">{analytics.overview?.total_subscriptions || 0}</p>
                  <p className="text-xs text-gray-500">Total Subs</p>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-orange-500">{analytics.overview?.pending_subscriptions || 0}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </CardContent></Card>
              </div>

              {/* Subscription Breakdown */}
              <Card className="border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Subscription Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-green-50 rounded-xl border border-green-200">
                      <p className="text-xl font-bold text-green-700">{analytics.overview?.paystack_verified || 0}</p>
                      <p className="text-xs text-gray-600 font-medium">Paystack Verified</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-xl border border-blue-200">
                      <p className="text-xl font-bold text-blue-700">{analytics.overview?.admin_activated || 0}</p>
                      <p className="text-xs text-gray-600 font-medium">Admin Activated</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-xl font-bold text-amber-700">{analytics.overview?.pending_subscriptions || 0}</p>
                      <p className="text-xs text-gray-600 font-medium">Pending Payment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Cards */}
              <Card className="border-purple-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Promotion Revenue (Paystack Verified)</span>
                    <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="reset-revenue-btn"
                      onClick={async () => {
                        if (!window.confirm('Reset promotion revenue stats? This sets a new starting point — no data is deleted.')) return;
                        try {
                          const res = await fetch(`${API_BASE}/api/admin/promotion/revenue/reset`, { method: 'POST', headers: getAuthHeaders() });
                          if (res.ok) { toast.success('Revenue stats reset'); fetchAnalytics(); }
                        } catch (e) { toast.error('Error resetting'); }
                      }}>
                      Reset Stats
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-purple-50 rounded-xl">
                      <p className="text-xl font-bold text-purple-700">GHS {(analytics.overview?.revenue_this_month || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">This Month</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-xl">
                      <p className="text-xl font-bold text-purple-700">GHS {(analytics.overview?.revenue_this_year || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">This Year</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-xl">
                      <p className="text-xl font-bold text-purple-700">GHS {(analytics.overview?.revenue || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{analytics.overview?.revenue_reset_date ? 'Since Reset' : 'All Time'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Paystack Payments */}
              {analytics.recent_payments?.length > 0 && (
                <Card className="border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Recent Paystack Payments</CardTitle>
                    <CardDescription>Verified successful transactions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b text-left text-xs text-gray-500">
                          <th className="pb-2 pr-3">User</th>
                          <th className="pb-2 pr-3">Rank</th>
                          <th className="pb-2 pr-3">Amount</th>
                          <th className="pb-2 pr-3">Reference</th>
                          <th className="pb-2">Date</th>
                        </tr></thead>
                        <tbody>
                          {analytics.recent_payments.map(p => (
                            <tr key={p.id} className="border-b">
                              <td className="py-2 pr-3">
                                <p className="font-medium">{p.user_name}</p>
                                <p className="text-xs text-gray-400">{p.user_email}</p>
                              </td>
                              <td className="py-2 pr-3">{p.rank_name}</td>
                              <td className="py-2 pr-3 font-medium text-green-700">{p.currency} {(p.amount || 0).toFixed(2)}</td>
                              <td className="py-2 pr-3 font-mono text-xs">{p.reference}</td>
                              <td className="py-2 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Per-Rank Stats */}
              <Card>
                <CardHeader><CardTitle className="text-base">Performance by Rank</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-xs text-gray-500">
                        <th className="pb-2 pr-3">Rank</th><th className="pb-2 pr-3">Users</th>
                        <th className="pb-2 pr-3">Questions</th><th className="pb-2 pr-3">Attempts</th>
                        <th className="pb-2">Avg Score</th>
                      </tr></thead>
                      <tbody>
                        {analytics.rank_stats?.map(r => (
                          <tr key={r.rank_id} className="border-b">
                            <td className="py-2 pr-3 font-medium">{r.rank_name}</td>
                            <td className="py-2 pr-3">{r.users_preparing}</td>
                            <td className="py-2 pr-3">{r.question_count}</td>
                            <td className="py-2 pr-3">{r.total_attempts}</td>
                            <td className="py-2"><Badge variant="outline" className={r.average_score >= 50 ? 'bg-green-50' : 'bg-red-50'}>{r.average_score}%</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Year Stats */}
              {analytics.year_stats?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Most Attempted Years</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.year_stats.map(y => (
                        <div key={y.year} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="font-medium">{y.year}</span>
                          <div className="flex gap-3 text-sm">
                            <span>{y.attempts} attempts</span>
                            <Badge variant="outline">{y.avg_score}% avg</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Most Difficult Questions */}
              {analytics.most_difficult_questions?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Most Difficult Questions</CardTitle><CardDescription>Lowest correct rate (min 3 attempts)</CardDescription></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.most_difficult_questions.slice(0, 10).map((q, i) => (
                        <div key={q.question_id} className="p-3 border rounded-lg">
                          <p className="text-sm font-medium mb-1">{q.question_text}</p>
                          <div className="flex gap-3 text-xs">
                            <span>Attempts: {q.attempts}</span>
                            <span className="text-green-600">Correct: {q.correct}</span>
                            <span className="text-red-600">Wrong: {q.wrong}</span>
                            <Badge variant="outline" className="bg-red-50 text-red-700">{q.correct_rate}% correct rate</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-gray-500">Loading analytics...</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* RANK DIALOG */}
      <Dialog open={rankDialog} onOpenChange={setRankDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Rank' : 'Create Rank'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Rank Name *</Label><Input value={rankForm.name} onChange={e => setRankForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Principal Superintendent" /></div>
            <div><Label>Description</Label><Input value={rankForm.description} onChange={e => setRankForm(p => ({...p, description: e.target.value}))} placeholder="Optional description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duration (minutes)</Label><Input type="number" value={rankForm.exam_duration_minutes} onChange={e => setRankForm(p => ({...p, exam_duration_minutes: e.target.value === '' ? '' : parseInt(e.target.value)}))} /></div>
              <div><Label>Number of Questions</Label><Input type="number" value={rankForm.num_questions} onChange={e => setRankForm(p => ({...p, num_questions: e.target.value === '' ? '' : parseInt(e.target.value)}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRankDialog(false)}>Cancel</Button>
            <Button onClick={saveRank} disabled={saving || !rankForm.name}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TOPIC DIALOG */}
      <Dialog open={topicDialog} onOpenChange={setTopicDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Topic' : 'Create Topic'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rank *</Label>
              <Select value={topicForm.rank_id} onValueChange={v => setTopicForm(p => ({...p, rank_id: v}))}>
                <SelectTrigger><SelectValue placeholder="Select rank" /></SelectTrigger>
                <SelectContent>{ranks.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Topic Name *</Label><Input value={topicForm.name} onChange={e => setTopicForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Educational Psychology" /></div>
            <div><Label>Description</Label><Input value={topicForm.description} onChange={e => setTopicForm(p => ({...p, description: e.target.value}))} placeholder="Optional" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopicDialog(false)}>Cancel</Button>
            <Button onClick={saveTopic} disabled={saving || !topicForm.rank_id || !topicForm.name}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QUESTION DIALOG */}
      <Dialog open={questionDialog} onOpenChange={setQuestionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Question' : 'Create Question'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rank *</Label>
                <Select value={questionForm.rank_id} onValueChange={v => setQuestionForm(p => ({...p, rank_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select rank" /></SelectTrigger>
                  <SelectContent>{ranks.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year *</Label>
                <Select value={String(questionForm.year)} onValueChange={v => setQuestionForm(p => ({...p, year: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                  <SelectContent>{[2020,2021,2022,2023,2024,2025,2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Topic</Label>
                <Select value={questionForm.topic_id || 'none'} onValueChange={v => setQuestionForm(p => ({...p, topic_id: v === 'none' ? '' : v}))}>
                  <SelectTrigger><SelectValue placeholder="Select topic" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No topic</SelectItem>
                    {topics.filter(t => !questionForm.rank_id || t.rank_id === questionForm.rank_id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={questionForm.difficulty} onValueChange={v => setQuestionForm(p => ({...p, difficulty: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Question Text *</Label>
              <textarea className="w-full border rounded-md p-2 min-h-[80px] text-sm" value={questionForm.question_text} onChange={e => setQuestionForm(p => ({...p, question_text: e.target.value}))} placeholder="Enter the question..." />
            </div>
            <div className="space-y-2">
              <Label>Answer Options *</Label>
              {['A', 'B', 'C', 'D'].map(opt => (
                <div key={opt} className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${questionForm.correct_option === opt ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{opt}</span>
                  <Input
                    value={questionForm[`option_${opt.toLowerCase()}`]}
                    onChange={e => setQuestionForm(p => ({...p, [`option_${opt.toLowerCase()}`]: e.target.value}))}
                    placeholder={`Option ${opt}`}
                    className="flex-1"
                  />
                </div>
              ))}
            </div>
            <div>
              <Label>Correct Answer *</Label>
              <div className="flex gap-2 mt-1">
                {['A', 'B', 'C', 'D'].map(opt => (
                  <Button key={opt} type="button" size="sm"
                    variant={questionForm.correct_option === opt ? 'default' : 'outline'}
                    className={questionForm.correct_option === opt ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setQuestionForm(p => ({...p, correct_option: opt}))}
                  >{opt}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Explanation (Optional)</Label>
              <textarea className="w-full border rounded-md p-2 min-h-[60px] text-sm" value={questionForm.explanation} onChange={e => setQuestionForm(p => ({...p, explanation: e.target.value}))} placeholder="Why this is the correct answer..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuestionDialog(false)}>Cancel</Button>
            <Button onClick={saveQuestion} disabled={saving}>{saving ? 'Saving...' : 'Save Question'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IMPORT DIALOG */}
      <Dialog open={importDialog} onOpenChange={(v) => { setImportDialog(v); if (!v) { setImportStep('form'); setImportPreview(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>CSV Import Questions</DialogTitle>
          </DialogHeader>

          {importStep === 'form' && (
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700 space-y-1">
                <p className="font-semibold">CSV Format by Question Type:</p>
                {importQType === 'multiple_choice' && <p>Columns: <code>question_text, option_a, option_b, option_c, option_d, correct_answer</code><br/>correct_answer = A, B, C, or D</p>}
                {importQType === 'true_false' && <p>Columns: <code>question_text, correct_answer</code><br/>correct_answer = True or False</p>}
                {importQType === 'fill_in' && <p>Columns: <code>question_text, correct_answer</code><br/>correct_answer = the expected text answer</p>}
                <p className="mt-1 text-gray-500">Optional columns: explanation, topic, difficulty</p>
                <p className="text-gray-500">Question numbers are assigned automatically.</p>
              </div>
              <div>
                <Label>Rank *</Label>
                <Select value={importRank} onValueChange={setImportRank}>
                  <SelectTrigger data-testid="import-rank-select"><SelectValue placeholder="Select rank" /></SelectTrigger>
                  <SelectContent>{ranks.map(r => <SelectItem key={r.id} value={r.id} data-testid={`import-rank-option-${r.id}`}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year *</Label>
                <Select value={importYear} onValueChange={setImportYear}>
                  <SelectTrigger data-testid="import-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
                  <SelectContent>{[2020,2021,2022,2023,2024,2025,2026].map(y => <SelectItem key={y} value={String(y)} data-testid={`import-year-option-${y}`}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Question Type *</Label>
                <Select value={importQType} onValueChange={setImportQType}>
                  <SelectTrigger data-testid="import-qtype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice" data-testid="import-qtype-multiple_choice">Multiple Choice</SelectItem>
                    <SelectItem value="true_false" data-testid="import-qtype-true_false">True / False</SelectItem>
                    <SelectItem value="fill_in" data-testid="import-qtype-fill_in">Fill-in-the-Gap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CSV File *</Label>
                <Input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])} data-testid="csv-file-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportDialog(false)}>Cancel</Button>
                <Button onClick={handleImportPreview} disabled={saving || !importFile || !importRank || !importYear} data-testid="csv-preview-btn">
                  {saving ? 'Validating...' : 'Validate & Preview'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {importStep === 'preview' && importPreview && (
            <div className="space-y-3">
              {importPreview.valid ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl space-y-2" data-testid="import-preview-success">
                  <h3 className="font-bold text-green-800">Import Preview</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-600">Rank:</div><div className="font-medium">{importPreview.rank_name}</div>
                    <div className="text-gray-600">Year:</div><div className="font-medium">{importPreview.year}</div>
                    <div className="text-gray-600">Question Type:</div><div className="font-medium capitalize">{importPreview.question_type.replace('_', ' ')}</div>
                    <div className="text-gray-600">Questions detected:</div><div className="font-medium">{importPreview.valid_count}</div>
                    <div className="text-gray-600">Starting number:</div><div className="font-medium">{importPreview.start_number}</div>
                    <div className="text-gray-600">Ending number:</div><div className="font-medium">{importPreview.end_number}</div>
                  </div>
                  <p className="text-sm text-green-700 font-medium mt-2">
                    Questions {importPreview.start_number}–{importPreview.end_number} will be created.
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-2" data-testid="import-preview-errors">
                  <h3 className="font-bold text-red-800">Import Failed — Validation Errors</h3>
                  <p className="text-sm text-red-600">No questions will be imported. Fix the errors below and re-upload.</p>
                  <div className="max-h-48 overflow-y-auto space-y-1 mt-2">
                    {(importPreview.errors || []).map((e, i) => (
                      <div key={i} className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded">
                        Row {e.row}: {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importPreview.duplicate_warning && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="import-duplicate-warning">
                  <strong>Warning:</strong> {importPreview.rank_name} {importPreview.year} already contains {importPreview.existing_same_type} {importPreview.question_type.replace('_', ' ')} question(s). Importing may create duplicates.
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => { setImportStep('form'); setImportPreview(null); }}>
                  Back
                </Button>
                {importPreview.valid && (
                  <Button onClick={handleImportConfirm} disabled={saving} data-testid="csv-confirm-import-btn">
                    {saving ? 'Importing...' : 'Confirm Import'}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>}
    </div>
  );
};

export default PromotionQuestionBank;
